import { motion } from "framer-motion";

const CHAPTERS = [
    {
        numeral: "I",
        title: "The Garden",
        quote: "Your strength is not for ruling over life. It is for protecting it.",
        body: "Eve was alone in Eden, and she was whole. Then life quickened within her — a mystery the story never explains — and she gave birth to a son. She raised him, loved him, taught him that strength is for protecting life. Every day he placed a rose beside her ear, because to him she was the most beautiful flower in the garden. When she died, he dug her grave with his own hands, and whispered the words the serpent was waiting to hear: I wish I didn't have to feel this.",
    },
    {
        numeral: "II",
        title: "The Legacy",
        quote: "Don't cry. Don't feel. Be a man.",
        body: "The wound becomes the world. Protection slowly becomes control, and the serpent's lesson passes from father to son for a thousand generations. Kings. Wars. Conquest. A five-year-old boy falls in a park, scrapes his knee, and is told to get up and hide his tears. The problem was never masculine strength — it is strength separated from compassion.",
    },
    {
        numeral: "III",
        title: "The Remembering",
        quote: "Eve came first. Adam came through her.",
        body: "The corrective mirror is held up: his first teacher was a woman, his first love was his mother — and yet the world placed the masculine above the feminine. Then the pattern repeats: two mothers, two sons, two mysteries. Mary remembers Eve, and her son weeps, touches the untouchable, and proves that vulnerability and strength were never enemies.",
    },
    {
        numeral: "IV",
        title: "The Return",
        quote: "What hurts?",
        body: "The boy from the park grows into a lonely man — until his own daughter falls, and he kneels, and asks the question Eve once asked. The wall cracks. He turns to his aging father and asks with compassion: Dad, what hurt you? And in a church basement, six broken men sit in a circle and say I'm scared, and another says so am I. This is the quiet uprising.",
    },
    {
        numeral: "V",
        title: "The Remembrance",
        quote: "The garden is not a place. It is within you. It always was.",
        body: "Mary sings after everything is lost, and the song keeps the flame alive. The rose is remembered — it grows in the cracks, it grows in you. A father forgives himself, and his daughter places a rose in his hand. The serpent is defeated — not forever, but here, in this house, in this heart. Eve came first. And she is coming back.",
    },
];

export default function ManifestoChapters() {
    return (
        <section id="story" data-testid="manifesto-section" className="relative mx-auto max-w-5xl px-6 py-32 sm:py-44">
            <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1 }}
                className="font-cinzel mb-24 text-center text-xs tracking-[0.45em] text-[#b89860] uppercase"
            >
                The Story — In Five Movements
            </motion.p>

            <div className="space-y-32 sm:space-y-40">
                {CHAPTERS.map((ch, i) => (
                    <motion.article
                        key={ch.numeral}
                        data-testid={`chapter-${i + 1}`}
                        initial={{ opacity: 0, y: 60 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className={`grid items-start gap-10 sm:grid-cols-12 ${i % 2 === 1 ? "sm:text-right" : ""}`}
                    >
                        <div className={`sm:col-span-3 ${i % 2 === 1 ? "sm:order-2" : ""}`}>
                            <span className="font-cinzel block text-6xl text-[rgba(212,175,55,0.35)] sm:text-7xl">
                                {ch.numeral}
                            </span>
                            <div className={`gold-rule mt-6 hidden sm:block ${i % 2 === 1 ? "ml-auto" : ""} w-16`} />
                        </div>
                        <div className={`sm:col-span-9 ${i % 2 === 1 ? "sm:order-1" : ""}`}>
                            <h2 className="font-cinzel text-2xl tracking-wide text-[#f3e5ab] sm:text-4xl">
                                {ch.title}
                            </h2>
                            <p className="font-cormorant mt-6 text-xl italic leading-relaxed text-[#d4c4a0] sm:text-2xl">
                                {ch.quote}
                            </p>
                            <p className={`font-cormorant drop-cap mt-6 max-w-2xl text-base leading-relaxed text-[#a3b899] sm:text-lg ${i % 2 === 1 ? "sm:ml-auto" : ""}`}>
                                {ch.body}
                            </p>
                        </div>
                    </motion.article>
                ))}
            </div>
        </section>
    );
}
